import {Routes} from '@angular/router';
import {AdminPanelComponent} from "@app/pages/admin-panel/admin-panel.component";
import {LeasingPosComponent} from "@app/pages/admin-panel/leasing-pos/leasing-pos.component";
import {LeasingUploadComponent} from "@app/pages/admin-panel/leasing-upload/leasing-upload.component";
import {FeatureConfigModulesComponent} from "@features/rbac-config/config-modules/feature-config-modules.component";
import {FeatureConfigRolesComponent} from "@features/rbac-config/config-roles/feature-config-roles.component";
import {FeatureConfigFeaturesComponent} from "@features/rbac-config/config-features/feature-config-features.component";
import {GrantsRoleGroupsComponent} from "@features/rbac-grants/grants-role-groups/grants-role-groups.component";
import {GrantsRolesComponent} from "@features/rbac-grants/grants-roles/grants-roles.component";

import {canActivate as canActivateAdmin} from "@app/guards/route/auth.guard";
import {LoginComponent} from "@features/authentication-login/login.component";
import {VerifyAccountComponent} from "@features/authentication/verify-account/verify-account.component";
import {canActivate as canActivatePath} from "@app/guards/path-context-guard.service";
import {
  RetailerTransactionStatementsComponent
} from '@app/pages/admin-panel/retailer-transaction-statement/retailer-transaction-statements.component';
import {
  RetailerExternalSoftwareStatementsComponent
} from '@app/pages/admin-panel/retailer-external-software-statements/retailer-external-software-statements.component';

export const routes: Routes = [

  {path: 'login', component: LoginComponent},
  {
    path: 'account/verify',
    component: VerifyAccountComponent,
    canActivate: [canActivatePath]
  },
  {
    path: '', component: AdminPanelComponent,
    canActivate: [canActivateAdmin],
    children: [
      {
        path: 'landlord',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Landlord'},
        children: [
          {
            path: 'settings',
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Landlord Settings'},
            children: [
              {path: '', redirectTo: 'details', pathMatch: 'full'},
              {
                path: 'details',
                loadComponent: () => import('./pages/admin-panel/landlord-settings/landlord-details/landlord-details.component')
                  .then(m => m.LandlordDetailsComponent),
                data: {breadcrumb: 'Details'},
              },
              {
                path: 'locations',
                loadComponent: () => import('./pages/admin-panel/landlord-settings/landlord-locations/landlord-locations.component')
                  .then(m => m.LandlordLocationsComponent),
                data: {breadcrumb: 'Locations'},
              },
              {
                path: 'access',
                loadComponent: () => import('./pages/admin-panel/landlord-settings/landlord-access/landlord-access.component')
                  .then(m => m.LandlordAccessComponent),
                data: {breadcrumb: 'Access'},
              },
              {
                path: 'contacts',
                loadComponent: () => import('./pages/admin-panel/landlord-settings/landlord-contacts/landlord-contacts.component')
                  .then(m => m.LandlordContactsComponent),
                data: {breadcrumb: 'Contacts'},
              },
            ],
          },
        ]
      },
      {
        path: 'location',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Location'},
        children: [
          {
            path: 'settings',
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Location Settings'},
            children: [
              {path: '', redirectTo: 'details', pathMatch: 'full'},
              {
                path: 'details',
                loadComponent: () => import('./pages/admin-panel/location-settings/location-details/location-details.component')
                  .then(m => m.LocationDetailsComponent),
                data: {breadcrumb: 'Details'},
              },
              {
                path: 'offerings',
                loadComponent: () => import('./pages/admin-panel/location-settings/location-offerings/location-offerings.component')
                  .then(m => m.LocationOfferingsComponent),
                data: {breadcrumb: 'Offerings'},
              },
              {
                path: 'access',
                loadComponent: () => import('./pages/admin-panel/location-settings/location-access/location-access.component')
                  .then(m => m.LocationAccessComponent),
                data: {breadcrumb: 'Access'},
              },
              {
                path: 'contacts',
                loadComponent: () => import('./pages/admin-panel/location-settings/location-contacts/location-contacts.component')
                  .then(m => m.LocationContactsComponent),
                data: {breadcrumb: 'Contacts'},
              },
            ],
          },
        ]
      },
      {
        path: 'offering',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Offering'},
        children: [
          {
            path: 'settings',
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Offering Settings'},
            children: [
              {path: '', redirectTo: 'details', pathMatch: 'full'},
              {
                path: 'details',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/offering-details/offering-details.component')
                  .then(m => m.OfferingDetailsComponent),
                data: {breadcrumb: 'Details'},
              },
              {
                path: 'recommendation',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/offering-recommendation/offering-recommendation.component')
                  .then(m => m.OfferingRecommendationComponent),
                data: {breadcrumb: 'Recommendation Config'},
              },
              {
                path: 'payments',
                loadComponent: () => import('./pages/admin-panel/integrated-pos/offering-payments/offering-payments.component')
                  .then(m => m.OfferingPaymentsComponent),
                data: {breadcrumb: 'Payments'},
              },
              {
                path: 'imports',
                loadComponent: () => import('./pages/admin-panel/integrated-pos/offering-imports/offering-imports.component')
                  .then(m => m.OfferingImportsComponent),
                data: {breadcrumb: 'Imports'},
              },
              {
                path: 'readiness',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/readiness-page/readiness-page.component')
                  .then(m => m.ReadinessPageComponent),
                data: {breadcrumb: 'Readiness'},
              },
              {
                path: 'billing-banking',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/billing-banking-tab/billing-banking-tab.component')
                  .then(m => m.BillingBankingTabComponent),
                data: {breadcrumb: 'Billing'},
              },
              {
                path: 'pos',
                loadComponent: () => import('./pages/admin-panel/integrated-pos/pos-connections-tab/pos-connections-tab.component')
                  .then(m => m.PosConnectionsTabComponent),
                data: {breadcrumb: 'POS Connections'},
              },
              {
                path: 'listing',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/listing-industries-tab/listing-industries-tab.component')
                  .then(m => m.ListingIndustriesTabComponent),
                data: {breadcrumb: 'Listing & Industries'},
              },
              {
                path: 'access',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/access-tab/access-tab.component')
                  .then(m => m.AccessTabComponent),
                data: {breadcrumb: 'Access'},
              },
              {
                path: 'contacts',
                loadComponent: () => import('./pages/admin-panel/onboarding-settings/offering-contacts/offering-contacts.component')
                  .then(m => m.OfferingContactsComponent),
                data: {breadcrumb: 'Contacts'},
              },
            ],
          },
        ]
      },
      {
        path: 'feature-config',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Global Configuration'},
        children: [
          {
            path: 'modules',
            component: FeatureConfigModulesComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Modules'}
          },

          {
            path: 'roles',
            component: FeatureConfigRolesComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Roles'}
          },
          {
            path: 'features',
            component: FeatureConfigFeaturesComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Features'}
          },
        ]
      },
      {
        path: 'rbac',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Role Based Access Control'},
        children: [
          {
            path: 'offering',
            data: {breadcrumb: 'Offering Grants'},
            children: [
              {
                path: 'role-groups',
                component: GrantsRoleGroupsComponent,
                canActivate: [canActivateAdmin],
                data: {breadcrumb: 'Role Groups'}
              },
              {
                path: 'roles',
                component: GrantsRolesComponent,
                canActivate: [canActivateAdmin],
                data: {breadcrumb: 'Individual Roles'}
              },
            ]
          },

        ]
      },
      {
        path: 'transactions',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Transactions'},
        children: [
          {
            path: 'statements',
            component: RetailerTransactionStatementsComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Statements'}
          },
          {
            path: 'external-accounts/software',
            component: RetailerExternalSoftwareStatementsComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'External Accounts'}
          }
        ]
      },
      {
        path: 'leasing',
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Leasing'},
        children: [
          {
            path: 'lease-upload',
            component: LeasingUploadComponent,
            canActivate: [canActivateAdmin],
            data: {
              breadcrumb: 'Upload',
            }
          },
          {
            path: 'billing-upload',
            component: LeasingUploadComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Upload'}
          },
          {
            path: 'lease-pos',
            component: LeasingPosComponent,
            canActivate: [canActivateAdmin],
            data: {breadcrumb: 'Point-of-Sale'}
          }
        ]
      },
      {
        path: 'jobs/configs',
        loadComponent: () => import('./pages/admin-panel/jobs/job-run-configs/job-run-configs.component')
          .then(m => m.JobRunConfigsComponent),
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Job Run Configs'},
      },
      {
        path: 'internal-access',
        loadComponent: () => import('./pages/internal-access/internal-access.component')
          .then(m => m.InternalAccessComponent),
        canActivate: [canActivateAdmin],
        data: {breadcrumb: 'Internal Access'},
      }
    ]
  },

];
